import { Controller } from '@hotwired/stimulus'

// Lazily loads Turbo Frame content for tab panels.
//
// This controller is designed to work together with tabs where each tab link
// can provide two params:
//   - url: the remote endpoint to load
//   - frame: the target turbo-frame id
//
// It loads a panel at most once per page lifecycle by setting
// `data-remote-panel-loaded="true"` on the frame after assigning its `src`.
//
// Usage:
//   data-controller="remote-panel"
//   data-action="click->remote-panel#load" on tab links
//   data-remote-panel-url-param="/path/to/panel"
//   data-remote-panel-frame-param="panel_frame_id"
export default class extends Controller {
  // On connect, tries to load the currently active tab panel (if configured).
  connect() {
    this.loadFromActiveTab()
  }

  // Loads a frame from event params.
  //
  // Expected params:
  //   event.params.url   → remote endpoint
  //   event.params.frame → frame id
  load(event) {
    const url = event?.params?.url
    const frameId = event?.params?.frame
    if (!url || !frameId) return

    const frame = this.findFrame(frameId)
    if (!frame) return
    if (frame.dataset.remotePanelLoaded === 'true') return

    frame.setAttribute('src', url)
    frame.dataset.remotePanelLoaded = 'true'
  }

  // Loads the panel corresponding to the active tab on initial render.
  // It looks for an active tab trigger that includes both remote params.
  loadFromActiveTab() {
    const activeTrigger = this.element.querySelector(
      '[data-tabs-target="tab"].active[data-remote-panel-url-param][data-remote-panel-frame-param], ' +
      '[data-tabs-target="tab"].active [data-remote-panel-url-param][data-remote-panel-frame-param]'
    )
    if (!activeTrigger) return

    this.load({
      params: {
        url: activeTrigger.dataset.remotePanelUrlParam,
        frame: activeTrigger.dataset.remotePanelFrameParam
      }
    })
  }

  // Finds a frame by id, first scoped to this controller root, then globally.
  findFrame(frameId) {
    return this.element.querySelector(`#${frameId}`) || document.getElementById(frameId)
  }
}
